import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell,
  RadialBarChart, RadialBar, AreaChart, Area
} from 'recharts';
import { FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiZap, FiTarget, FiAward, FiArrowUpRight, FiArrowDownRight } from 'react-icons/fi';

const C = {
  sky: '#7ea52e', purple: '#1a1a18', green: '#7ea52e',
  orange: '#9ec25a', red: '#1a1a18', cyan: '#b7d083', pink: '#5c7a1f', slate: '#7e7e71'
};

const TYPE_LABELS = {
  whatsapp: 'WhatsApp', whatsapp_image: 'WA Image', whatsapp_video: 'WA Video',
  whatsapp_document: 'WA Doc', sms: 'SMS', email: 'Email',
  rcs: 'RCS', newsletter: 'Newsletter', push_notification: 'Push',
  voice_call: 'Voice', status_messaging: 'Status', custom: 'Custom'
};
const TYPE_COLORS = {
  whatsapp: '#7ea52e', whatsapp_image: '#6b8f27', whatsapp_video: '#57721f',
  whatsapp_document: '#455a19', sms: '#7ea52e', email: '#1a1a18',
  rcs: '#b7d083', newsletter: '#5c7a1f', push_notification: '#7e7e71',
  voice_call: '#abab9f', status_messaging: '#161614', custom: '#c9c9c3'
};

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)',
      border: '1px solid rgba(126,165,46,0.12)', borderRadius: 14,
      padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    }}>
      <p style={{ color: '#0f172a', fontSize: 12, fontWeight: 700, marginBottom: 6, fontFamily: 'Poppins, sans-serif' }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.fill }} />
          <span style={{ color: '#475569', fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const CustomLegend = ({ payload }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, paddingTop: 8 }}>
    {payload?.map((e, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: e.color }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{e.value}</span>
      </div>
    ))}
  </div>
);

const PieCenter = ({ cx, cy, label, value }) => (
  <g>
    <text x={cx} y={cy - 6} textAnchor="middle" style={{ fontSize: 20, fontWeight: 800, fill: '#0f172a', fontFamily: 'Poppins' }}>{value}</text>
    <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 10, fontWeight: 600, fill: '#abab9f', letterSpacing: 1, textTransform: 'uppercase' }}>{label}</text>
  </g>
);

const PieLegend = ({ data }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0' }}>
    {data.map((d, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 10px', borderRadius: 8, background: 'var(--bg-primary)' }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#334155' }}>{d.name}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{d.value?.toLocaleString()}</span>
      </div>
    ))}
  </div>
);

const SectionHeader = ({ title, subtitle }) => (
  <div style={{ marginBottom: 14 }}>
    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>{title}</h3>
    {subtitle && <p style={{ fontSize: 11, color: '#abab9f', margin: '2px 0 0', fontWeight: 500 }}>{subtitle}</p>}
  </div>
);

export default function AdminInsights() {
  const [data, setData] = useState(null);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard'),
      api.get('/admin/insights-tips'),
    ]).then(([dashRes, tipsRes]) => {
      setData(dashRes.data);
      setTips(tipsRes.data?.tips || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!data) return null;

  const { stats = {}, channelBreakdown = [], topCampaigns = [] } = data;
  const totalMessages = (stats.totalSent || 0) + (stats.totalFailed || 0);
  const deliveryRate = totalMessages > 0 ? ((stats.totalSent / totalMessages) * 100).toFixed(1) : 0;
  const failRate = totalMessages > 0 ? ((stats.totalFailed / totalMessages) * 100).toFixed(1) : 0;

  /* ── Insight Cards ── */
  const insights = [
    {
      icon: <FiTrendingUp />, title: 'Delivery Rate', value: `${deliveryRate}%`,
      color: deliveryRate >= 90 ? C.green : deliveryRate >= 70 ? C.orange : C.red,
      bg: deliveryRate >= 90 ? '#eef3e6' : deliveryRate >= 70 ? '#e5edda' : '#f0f0ed',
      arrow: deliveryRate >= 70 ? <FiArrowUpRight /> : <FiArrowDownRight />,
      tip: deliveryRate >= 90 ? 'Excellent! Keep up the quality.' : deliveryRate >= 70 ? 'Good, but room for improvement.' : 'Attention needed! Review your data quality.',
    },
    {
      icon: <FiAlertTriangle />, title: 'Failure Rate', value: `${failRate}%`,
      color: failRate <= 5 ? C.green : failRate <= 15 ? C.orange : C.red,
      bg: failRate <= 5 ? '#eef3e6' : failRate <= 15 ? '#e5edda' : '#f0f0ed',
      arrow: failRate <= 5 ? <FiArrowDownRight /> : <FiArrowUpRight />,
      tip: failRate <= 5 ? 'Very low failure rate — great job!' : failRate <= 15 ? 'Moderate failures. Validate numbers.' : 'High failure rate. Audit master data.',
    },
    {
      icon: <FiTarget />, title: 'Campaign Utilization',
      value: `${stats.activeCampaigns || 0} / ${stats.totalCampaigns || 0}`,
      color: C.sky, bg: '#eef3e6',
      tip: stats.activeCampaigns > 0 ? `${stats.activeCampaigns} campaigns active.` : 'No active campaigns.',
    },
    {
      icon: <FiAward />, title: 'Data Coverage',
      value: `${(stats.totalMasterData || 0).toLocaleString()}`,
      color: C.purple, bg: '#f0f0ed',
      tip: (stats.totalMasterData || 0) >= 1000 ? 'Large dataset — ready for scale.' : 'Import more data for broader reach.',
    },
  ];

  /* Channel Performance */
  const channelPerf = (channelBreakdown || []).map(ch => {
    const total = (ch.totalSent || 0) + (ch.totalFailed || 0);
    return {
      channel: TYPE_LABELS[ch._id] || ch._id,
      Sent: ch.totalSent || 0, Failed: ch.totalFailed || 0,
      Delivery: total > 0 ? Math.round((ch.totalSent / total) * 100) : 0,
      color: TYPE_COLORS[ch._id] || C.slate
    };
  }).filter(c => c.Sent + c.Failed > 0);

  /* Task/Status Pie */
  const taskData = [
    { name: 'Delivered', value: stats.totalSent || 0, color: C.green },
    { name: 'Active', value: stats.activeCampaigns || 0, color: C.sky },
    { name: 'Failed', value: stats.totalFailed || 0, color: C.red },
  ].filter(d => d.value > 0);
  if (taskData.length === 0) taskData.push({ name: 'No Data', value: 1, color: '#e6e6e3' });
  const taskTotal = taskData.reduce((a, c) => a + c.value, 0);

  /* Delivery gauge */
  const gaugeVal = parseFloat(deliveryRate);
  const gaugeData = [{ name: 'Delivery', value: gaugeVal, fill: gaugeVal >= 90 ? C.green : gaugeVal >= 70 ? C.orange : C.red }];

  /* Dynamic tips from API */
  // tips already loaded via state

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Insights</h1>
          <p className="page-subtitle">Performance insights & recommendations</p>
        </div>
      </div>

      {/* ── Insight Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {insights.map((ins, i) => (
          <div key={i} className="card" style={{ borderLeft: `4px solid ${ins.color}`, overflow: 'hidden', position: 'relative' }}>
            {/* Subtle gradient bg */}
            <div style={{ position: 'absolute', top: 0, right: 0, width: 80, height: 80, borderRadius: '0 0 0 80px', background: ins.bg, opacity: 0.5 }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: ins.bg, color: ins.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                  {ins.icon}
                </div>
                {ins.arrow && <span style={{ color: ins.color, fontSize: 18 }}>{ins.arrow}</span>}
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#abab9f', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{ins.title}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: ins.color, fontFamily: 'Poppins, sans-serif', lineHeight: 1.1 }}>{ins.value}</div>
              <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0', lineHeight: 1.4 }}>{ins.tip}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Charts Row: Channel + Donut + Gauge ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.5fr', gap: 20, marginBottom: 24 }}>
        <div className="chart-container">
          <SectionHeader title="Channel Performance" subtitle="Sent vs Failed by channel" />
          {channelPerf.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={channelPerf} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="insAreaSent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7ea52e" stopOpacity={0.45} />
                    <stop offset="50%" stopColor="#9ec25a" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#b7d083" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="insAreaFailed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a1a18" stopOpacity={0.3} />
                    <stop offset="50%" stopColor="#333330" stopOpacity={0.1} />
                    <stop offset="100%" stopColor="#1a1a18" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 8" stroke="#e9ecef" vertical={false} />
                <XAxis dataKey="channel" tick={{ fill: '#abab9f', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#abab9f', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<GlassTooltip />} />
                <Legend content={<CustomLegend />} />
                <Area type="natural" dataKey="Sent" stroke="#7ea52e" fill="url(#insAreaSent)" strokeWidth={3}
                  dot={{ r: 5, fill: '#fff', stroke: '#7ea52e', strokeWidth: 2.5 }}
                  activeDot={{ r: 8, fill: '#7ea52e', stroke: '#fff', strokeWidth: 3 }}
                  animationDuration={1200} animationEasing="ease-in-out" />
                <Area type="natural" dataKey="Failed" stroke="#1a1a18" fill="url(#insAreaFailed)" strokeWidth={2.5}
                  dot={{ r: 4, fill: '#fff', stroke: '#1a1a18', strokeWidth: 2 }}
                  activeDot={{ r: 7, fill: '#1a1a18', stroke: '#fff', strokeWidth: 3 }}
                  animationDuration={1500} animationEasing="ease-in-out" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No channel data yet</h3></div>
          )}
        </div>

        <div className="chart-container">
          <SectionHeader title="Message Status" subtitle="Delivery overview" />
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie data={taskData} cx="50%" cy="50%" innerRadius={55} outerRadius={88} paddingAngle={3} dataKey="value" stroke="none" cornerRadius={4}>
                {taskData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip content={<GlassTooltip />} />
              {taskTotal > 0 && <PieCenter cx="50%" cy="50%" label="Status" value={taskTotal.toLocaleString()} />}
            </PieChart>
          </ResponsiveContainer>
          <PieLegend data={taskData} />
        </div>

        <div className="chart-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <SectionHeader title="Delivery" />
          <ResponsiveContainer width="100%" height={150}>
            <RadialBarChart cx="50%" cy="55%" innerRadius="55%" outerRadius="90%" startAngle={180} endAngle={0} data={gaugeData} barSize={12}>
              <RadialBar background={{ fill: '#f4f4f3' }} dataKey="value" cornerRadius={8} animationDuration={1500} />
              <Tooltip content={<GlassTooltip />} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: -16 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: gaugeData[0].fill, fontFamily: 'Poppins' }}>{deliveryRate}%</div>
            <div style={{ fontSize: 10, color: '#abab9f', fontWeight: 600 }}>SUCCESS</div>
          </div>
        </div>
      </div>

      {/* ── Tips Grid ── */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: '#e5edda', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.orange, fontSize: 16 }}><FiZap /></div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins' }}>Performance Tips</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {tips.slice(0, 6).map((tip, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, padding: '14px 16px', borderRadius: 14,
              background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
              transition: 'all 0.2s', cursor: 'default',
            }}>
              <span style={{ fontSize: 26, lineHeight: 1 }}>{tip.icon || '💡'}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{tip.title}</div>
                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{tip.desc}</div>
              </div>
            </div>
          ))}
          {tips.length === 0 && (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 20, color: '#abab9f', fontSize: 13 }}>Loading tips...</div>
          )}
        </div>
      </div>
    </div>
  );
}
